import React, { useState, useEffect, useMemo } from 'react';
import { Flex, Box } from '@chakra-ui/react';
import { SingleValue } from 'react-select';
import { format, parse } from 'date-fns';
import { KSelect } from '@/ui-library/components/molecules/KSelect';
import { KText } from '@/ui-library/components';

interface CustomTimeSelectorProps {
  date: Date | null;
  value: string | null;
  onChange: (value: string) => void;
  timeFormat: string;
}

interface TimeOption {
  value: string;
  label: string;
}

const generateTimes = (
  intervalInMinutes = 30,
  is24Hour: boolean
): TimeOption[] => {
  const times: TimeOption[] = [];
  for (let i = 0; i < 24 * 60; i += intervalInMinutes) {
    const hours = Math.floor(i / 60);
    const minutes = i % 60;
    const value = `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}`;
    const date = new Date(2000, 0, 1, hours, minutes);
    const label = format(date, is24Hour ? 'HH:mm' : 'h:mm a');
    times.push({ value, label });
  }
  return times;
};

export const CustomTimeSelector: React.FC<CustomTimeSelectorProps> = ({
  date,
  value,
  onChange,
  timeFormat,
}) => {
  const is24Hour = timeFormat === 'HH:mm';

  const timesArray = useMemo(() => generateTimes(30, is24Hour), [is24Hour]);
  const [filteredTimesArray, setFilteredTimesArray] =
    useState<TimeOption[]>(timesArray);

  const currentDate = new Date(date);
  const hours = currentDate.getHours();
  const minutes = currentDate.getMinutes();
  const currentTime = `${hours}:${minutes}`;

  useEffect(() => {
    if (!date) return;

    const currentDate = new Date();
    const isToday = currentDate.toDateString() === date.toDateString();
    const currentTimeInMillis =
      currentDate.getHours() * 3600000 + currentDate.getMinutes() * 60000;

    const newFilteredTimesArray = timesArray.filter((time) => {
      const [hours, minutes] = time.value.split(':').map(Number);
      const timeInMillis = hours * 3600000 + minutes * 60000;
      return !isToday || timeInMillis > currentTimeInMillis;
    });

    setFilteredTimesArray(newFilteredTimesArray);

    if (!value) {
      let defaultTime = newFilteredTimesArray.find(
        (item) => item.value === '09:00'
      );
      if (!defaultTime) {
        defaultTime = newFilteredTimesArray[0];
      }
      if (defaultTime) {
        onChange(defaultTime.value);
      }
    }
  }, [date, timesArray, value, onChange]);

  const handleSelectOnChange = (selectedValue: SingleValue<TimeOption>) => {
    if (selectedValue) {
      onChange(selectedValue.value);
    }
  };

  const formatOptionLabel = ({ value, label }: TimeOption) => {
    const date = parse(value, 'HH:mm', new Date());
    return format(date, is24Hour ? 'HH:mm' : 'h:mm a');
  };

  const selectedTime = value
    ? filteredTimesArray.find((item) => item.value === value)
    : filteredTimesArray.find((item) => item.value === currentTime);

  return (
    <Flex w="100%" gap="6px" flexDir="column">
      <KText color="secondary-900" size="sm-medium">
        Time
      </KText>
      <Box w="100%">
        <KSelect
          placeholder="Select time"
          value={selectedTime}
          options={filteredTimesArray}
          onChange={handleSelectOnChange}
          formatOptionLabel={formatOptionLabel}
        />
      </Box>
    </Flex>
  );
};
